/**
 * The camera folder watcher, replacing agent/internal/watcher.
 *
 * This is the one place the port gives ground rather than gaining it. The agent
 * used fsnotify — a push from the OS the instant the tethering software closed a
 * file — with a 3s sweep behind it only because fsnotify misses events on
 * network drives. A browser has no such API, so this is back to the desktop
 * app's `Directory.GetFiles` every 3s, which CLAUDE.md §6 lists as a thing the
 * port had fixed. Reading a directory handle is cheap enough that an operator
 * will not notice the difference, but it is a regression and not a rewrite.
 *
 * The readiness rules ARE carried over, because they are what stop a half-
 * written JPEG reaching the gallery:
 *
 *  - A file is announced only once its size has stopped changing between two
 *    passes. That is the browser's version of `WaitForFileReady`, which opened
 *    the file to see whether the camera still held it — a handle test has no
 *    equivalent here, but a settled size answers the same question.
 *  - Files older than 5s skip the gate entirely, exactly as the agent does.
 *    The check is right for a frame the camera is saving now and pure waste for
 *    a folder that is already complete — and pointing at a 482-photo folder is
 *    the normal way an event starts.
 *
 * Each photo keeps its handle here rather than in the store: `ImageFile` is
 * core/ wire shape and must stay framework-free and serialisable, so the
 * registry lives beside the watcher and `readPhoto` is how a file is opened.
 */

import { isSupportedImage, newIdentifier } from '@/core/session'
import type { ImageFile } from '@/core/types'

const POLL_MS = 3_000

/** Below this age a file must prove its size has settled. See above. */
const READY_GRACE_MS = 5_000

interface Seen {
  file: ImageFile
  handle: FileSystemFileHandle
  size: number
  /** False until the size has settled, or the file was already old on arrival. */
  announced: boolean
}

const seen = new Map<string, Seen>()

let timer: ReturnType<typeof setInterval> | undefined
let directory: FileSystemDirectoryHandle | null = null
let polling = false

export type WatchEvent =
  | { type: 'file.new'; file: ImageFile }
  | { type: 'watch.state'; running: boolean }
  | { type: 'watch.error'; error: string }

type Listener = (event: WatchEvent) => void

const listeners = new Set<Listener>()

export function onWatchEvent(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function emit(event: WatchEvent): void {
  for (const listener of listeners) listener(event)
}

export function isWatching(): boolean {
  return timer !== undefined
}

/** Everything announced so far, so a reload does not lose the gallery. */
export function knownFiles(): ImageFile[] {
  return [...seen.values()].filter((entry) => entry.announced).map((entry) => entry.file)
}

/**
 * Opens a photo's bytes.
 *
 * Returns null when the file has gone — the operator moved or deleted it
 * mid-session, which on an event floor happens — so callers can drop it rather
 * than fail a whole composite.
 */
export async function readPhoto(file: ImageFile): Promise<File | null> {
  const entry = seen.get(file.filePath.toLowerCase())
  if (!entry) return null
  try {
    return await entry.handle.getFile()
  } catch {
    return null
  }
}

export async function startWatch(handle: FileSystemDirectoryHandle): Promise<void> {
  stopWatch()

  // A different folder than the grid is showing means the previous folder's
  // photos are no longer openable. The agent forgot them on start for exactly
  // this reason: selecting a file it could not serve failed at composite time.
  if (directory && directory !== handle) seen.clear()
  directory = handle

  timer = setInterval(() => {
    void poll()
  }, POLL_MS)
  emit({ type: 'watch.state', running: true })

  await poll()
}

export function stopWatch(): void {
  if (timer) clearInterval(timer)
  timer = undefined
  emit({ type: 'watch.state', running: false })
}

/** One pass, also used by the manual "Muat ulang" button. */
export async function poll(): Promise<void> {
  if (!directory || polling) return
  polling = true

  try {
    const now = Date.now()

    for await (const entry of directory.values()) {
      if (entry.kind !== 'file' || !isSupportedImage(entry.name)) continue

      const handle = entry as FileSystemFileHandle
      const key = entry.name.toLowerCase()

      let file: File
      try {
        file = await handle.getFile()
      } catch {
        // Still being written, or locked by the tethering software. The next
        // pass will pick it up; this must not take down the whole sweep.
        continue
      }

      const existing = seen.get(key)

      if (!existing) {
        const settled = now - file.lastModified > READY_GRACE_MS
        const record: Seen = {
          handle,
          size: file.size,
          announced: settled,
          file: {
            filePath: entry.name,
            fileName: entry.name,
            // The File API exposes no creation time; `lastModified` is the
            // closest equivalent and orders a camera folder identically.
            createdAt: file.lastModified,
            identifier: newIdentifier(),
          },
        }
        seen.set(key, record)
        if (settled) emit({ type: 'file.new', file: record.file })
        continue
      }

      // Already announced: nothing more to decide.
      if (existing.announced) continue

      // Held back last pass. A size that has stopped moving means the camera
      // has finished writing.
      if (file.size === existing.size && file.size > 0) {
        existing.announced = true
        emit({ type: 'file.new', file: existing.file })
      } else {
        existing.size = file.size
      }
    }
  } catch (error) {
    // A revoked permission lands here, and it is the one failure the operator
    // must not read as "no photos yet".
    emit({ type: 'watch.error', error: error instanceof Error ? error.message : String(error) })
  } finally {
    polling = false
  }
}

/** Forgets every photo. Used when the watched folder changes. */
export function resetWatch(): void {
  seen.clear()
  directory = null
}
